import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Button, Checkbox, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { styled } from '@mui/system';
import { Remove, Add } from '@mui/icons-material';
import { AuthContext } from "../auth/Auth.jsx";
import { COLLECTION_TYPES, CollectionType, useCollections } from '../global-state/useCollections.ts';
import { useEnsureProjects } from '../hooks/useEnsureProjects';

const FilterIconButton = styled(IconButton)(({ theme }) => ({  height:'36px', borderRadius:'10%', float:'right', cssFloat: 'right'}))

interface ProjectItem {
   id: string;
   name: string;
   count: number;
   isRefined: boolean;
   type: CollectionType;
}

interface ProjectFilterProps {
   onProjectFiltersChange: (projectIds: string[]) => void;
   onIndividualCollectionFiltersChange: (filters: { myIndividual: boolean, sharedIndividual: boolean }) => void;
   collections?: any[]; // Adding collections to trigger data refresh when they change
   isTeamsProUser?: boolean; // Teams PRO users can see all collections
   isRegularProUser?: boolean; // Regular PRO users can see individual + project collections
}

export function ProjectFilter({ onProjectFiltersChange, onIndividualCollectionFiltersChange, collections, isTeamsProUser, isRegularProUser }: ProjectFilterProps) {
   const { currentUser } = useContext(AuthContext);
   const [myIndividualSelected, setMyIndividualSelected] = useState<boolean>(false);
   const [sharedIndividualSelected, setSharedIndividualSelected] = useState<boolean>(false);
   const [refinedProjectIds, setRefinedProjectIds] = useState<string[]>([]);

   // ✅ Use existing hooks following CollectionPopover pattern
   const { projects, ownedProjects, sharedProjects: rawSharedProjects, isProjectsLoading } = useEnsureProjects();
   
   // ✅ Use proper Zustand selectors to subscribe to state changes
    const { ownedCollections, sharedCollections, projectCounts } = useCollections(
       (state) => ({
           ownedCollections: state.getOwnedCollections(),
           sharedCollections: state.getSharedCollections(),
           projectCounts: state.getProjectsWithCollectionCounts()
       })
   );

   // ✅ Compute project data with collection counts using memoization (CollectionPopover pattern)
   const projectsWithCounts = useMemo(() => {
       // Filter out archived projects and add collection counts
       const myProjectsWithCounts: ProjectItem[] = ownedProjects
           .filter(project => !project.archive)
           .map(project => ({
               id: project.id,
               name: project.name || 'Loading...',
               count: projectCounts.get(project.id) || 0,
               isRefined: refinedProjectIds.includes(project.id),
               type: COLLECTION_TYPES.OWNED_PROJECT
           }));

       const sharedProjectsWithCounts: ProjectItem[] = isTeamsProUser 
           ? rawSharedProjects
               .filter(project => !project.archive)
               .map(project => ({
                   id: project.id,
                   name: project.name || 'Loading...',
                   count: projectCounts.get(project.id) || 0,
                   isRefined: refinedProjectIds.includes(project.id),
                   type: COLLECTION_TYPES.SHARED_PROJECT
               }))
           : [];

       return {
           myProjects: myProjectsWithCounts,
           sharedProjects: sharedProjectsWithCounts
       };
   }, [ownedProjects, rawSharedProjects, projectCounts, refinedProjectIds, isTeamsProUser]);

   // ✅ Get collection counts using direct data from store
   const collectionCounts = useMemo(() => ({
        myIndividualCount: ownedCollections.filter(c => c.collectionType === COLLECTION_TYPES.INDIVIDUAL).length,
       sharedIndividualCount: sharedCollections.filter(c => c.collectionType === COLLECTION_TYPES.SHARED).length
   }), [ownedCollections, sharedCollections]);

   const handleClear = () => {
       setRefinedProjectIds([]);
       setMyIndividualSelected(false);
       setSharedIndividualSelected(false);
       onProjectFiltersChange([]);
       onIndividualCollectionFiltersChange({ myIndividual: false, sharedIndividual: false });
   };

   const handleIndividualFilterChange = (type: CollectionType) => {
       if (type === COLLECTION_TYPES.INDIVIDUAL) {
           const newValue = !myIndividualSelected;
           setMyIndividualSelected(newValue);
           onIndividualCollectionFiltersChange({
               myIndividual: newValue,
               sharedIndividual: sharedIndividualSelected
           });
       } else {
           const newValue = !sharedIndividualSelected;
           setSharedIndividualSelected(newValue);
           onIndividualCollectionFiltersChange({
               myIndividual: myIndividualSelected,
               sharedIndividual: newValue
           });
       }
   };

   const handleProjectFilterChange = (project: ProjectItem) => {
       const newRefinedIds = project.isRefined 
           ? refinedProjectIds.filter(id => id !== project.id)
           : [...refinedProjectIds, project.id];
       
       setRefinedProjectIds(newRefinedIds);
       onProjectFiltersChange(newRefinedIds);
   };

   if (!currentUser) return null;

   const { myProjects, sharedProjects } = projectsWithCounts;
   const { myIndividualCount, sharedIndividualCount } = collectionCounts;

   return (
       <>
           <Grid item xs={12} style={{display:'flex', justifyContent:'center', height:'45px', paddingTop:'9px'}}>
               <Tooltip title="Clear Filters" arrow>
                   <FilterIconButton onClick={handleClear} data-testid="clear-filters-button">
                       <Typography style={{paddingRight:'3px'}} variant='caption'>Clear Filters</Typography>
                       <FilterAltOffIcon color="action" />
                   </FilterIconButton> 
               </Tooltip>
           </Grid>
           <Grid item xs={12}><Divider style={{paddingTop:'9px'}} /></Grid>
          
           {/* My Projects Section - Show for Regular PRO and Teams PRO users */}
           {(isRegularProUser || isTeamsProUser) && (
               <ProjectDropdown
                   attributeName='In My Projects'
                   items={myProjects}
                   onFilterChange={handleProjectFilterChange}
                   loading={isProjectsLoading}
               />
           )}
          
           {/* Shared Projects Section - Show only for Teams PRO users */}
           {isTeamsProUser && (
               <ProjectDropdown
                   attributeName='In Shared Projects'
                   items={sharedProjects}
                   onFilterChange={handleProjectFilterChange}
                   loading={isProjectsLoading}
               />
           )}
          
           {/* My Individual Collections Section - Show for all users */}
           <IndividualCollectionSection
               attributeName='My Individual Collections'
               count={myIndividualCount}
               isSelected={myIndividualSelected}
               onFilterChange={() => handleIndividualFilterChange(COLLECTION_TYPES.INDIVIDUAL)}
               loading={isProjectsLoading}
           />
          
           {/* Shared Individual Collections Section - Show only for Teams PRO users */}
           {isTeamsProUser && (
               <IndividualCollectionSection
                   attributeName='Shared Individual Collections'
                   count={sharedIndividualCount}
                   isSelected={sharedIndividualSelected}
                   onFilterChange={() => handleIndividualFilterChange(COLLECTION_TYPES.SHARED)}
                   loading={isProjectsLoading}
               />
           )}
       </>
   );
}

interface ProjectDropdownProps {
   attributeName: string;
   items: ProjectItem[];
   onFilterChange: (project: ProjectItem) => void;
   loading: boolean;
}

function ProjectDropdown({ attributeName, items, onFilterChange, loading }: ProjectDropdownProps) {
   const hasItemsWithCollections = items.some(item => item.count > 0);

   // Don't render if no projects have collections
   if (!hasItemsWithCollections) {
       return null;
   }

   return (
       <Grid container alignItems="center">   
           <Grid item xs={12}>
               <Typography style={{color:'black', marginBottom: '2px'}}>{attributeName}</Typography>
           </Grid>
          
           {/* All items in original order */}
           <Grid item xs={12} sx={{maxHeight:'calc(74.5vh - 270px)', overflowX:'clip', overflowY:'auto'}}>
               {items.filter(item => item.count > 0).map((item, index) => (
                   <Grid item xs={12} key={`item-${index}`} style={{height:'29px', whiteSpace: 'nowrap', overflow: 'hidden'}}>
                       <Checkbox
                           size='medium'
                           style={{padding: '7px'}}
                           checked={item.isRefined}
                           onChange={() => onFilterChange(item)}
                           disabled={loading}
                           aria-label={`${item.name} (${item.count})`}
                       />
                       <Typography variant='caption' style={{color:'black'}}>
                           {item.name} ({item.count})
                       </Typography>
                   </Grid>
               ))}
           </Grid>
          
           <Grid item xs={12}>
               <Divider style={{paddingTop:'9px'}} />
           </Grid>
       </Grid>
   );
}

interface IndividualCollectionSectionProps {
   attributeName: string;
   count: number;
   isSelected: boolean;
   onFilterChange: () => void;
   loading: boolean;
}

function IndividualCollectionSection({ attributeName, count, isSelected, onFilterChange, loading }: IndividualCollectionSectionProps) {
   // Don't render if no collections are present
   if (count === 0) {
       return null;
   }

   return (
       <Grid container alignItems="center">   
           <Grid item xs={12} style={{minHeight:'29px', display: 'flex', alignItems: 'flex-start'}}>
           <Checkbox
                   size='medium'
                   style={{padding:'7px', flexShrink: 0}}
                   checked={isSelected}
                   onChange={onFilterChange}
                   disabled={loading}
                   aria-label={`${attributeName} (${count})`}
               />
           <Typography
                   style={{
                       color:'black',
                       cursor: 'pointer',
                       lineHeight: '1.2',
                       wordWrap: 'break-word',
                       overflowWrap: 'break-word',
                       marginTop: '9px'
                   }}
                   onClick={onFilterChange}
               >
                   {attributeName} ({count})
               </Typography>
             
           </Grid>
           <Grid item xs={12}>
               <Divider style={{paddingTop:'9px'}} />
           </Grid>
       </Grid>
   );
}

